import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CyberCard from '../components/CyberCard';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiClock, FiList, FiHeadphones, FiZap, FiLock, FiStar, FiCheckCircle, 
  FiAlertTriangle, FiInfo, FiLayers, FiFilter, FiChevronRight, FiChevronLeft,
  FiHelpCircle, FiTarget, FiCpu, FiShield, FiFileText
} = FiIcons;

const WORKFLOW_STEPS = [
  { id: 1, title: 'Session Initialization', icon: FiLayers, desc: 'Define your focus window. Select a duration (25-90m) and frequency. This primes the neural environment for high-intensity work.' },
  { id: 2, title: 'Task Calibration', icon: FiList, desc: 'Populate your Task Stack. FocusForge filters tasks by energy requirement, ensuring you tackle complex logic when cognitive reserves are peak.' },
  { id: 3, title: 'Neural Engagement', icon: FiHeadphones, desc: 'Activate the Soundscape Picker. Integrated masking frequencies (Brown/Pink noise) eliminate environmental auditory distractions.' },
  { id: 4, title: 'The Deep Sprint', icon: FiZap, desc: 'Enter the Session Cockpit. Hard Mode restricts access to peripheral apps and silences system-level notifications automatically.' },
  { id: 5, title: 'Distraction Vaulting', icon: FiLock, desc: 'As intrusive thoughts occur, log them instantly into the Calm Vault. This prevents context switching while ensuring ideas are captured.' },
  { id: 6, title: 'Cognitive Synthesis', icon: FiCheckCircle, desc: 'Post-session review. Quantify your output, note friction points, and log completed items into the permanent record.' },
  { id: 7, title: 'Streak Sync', icon: FiStar, desc: 'Data is pushed to your Streak Rails. Consistency metrics are updated, visualizing your momentum across the work week.' },
];

const SAMPLE_SESSIONS = [
  { id: 'S-204', date: '2024-05-22', label: 'Smart Contract Audit', duration: '90m', status: 'Optimal', focus: 98, notes: 'Full flow achieved.' },
  { id: 'S-203', date: '2024-05-22', label: 'UI Component Refactor', duration: '45m', status: 'Optimal', focus: 94, notes: 'Clean implementation.' },
  { id: 'S-202', date: '2024-05-21', label: 'E2E Testing Logic', duration: '60m', status: 'Drifted', focus: 76, notes: 'Interrupted by delivery.' },
  { id: 'S-201', date: '2024-05-21', label: 'Documentation Sprint', duration: '30m', status: 'Optimal', focus: 91, notes: 'Concise and fast.' },
  { id: 'S-200', date: '2024-05-20', label: 'Weekly Planning', duration: '120m', status: 'Interrupted', focus: 42, notes: 'Multiple Slack pings.' },
];

const FAQS = [
  { q: "How do I initiate my first session?", a: "Navigate to the Cockpit, select your duration, and hit 'Initialize'. The system will automatically engage your preset soundscape and enter Hard Mode if configured." },
  { q: "Is my focus data stored locally or in the cloud?", a: "By default, FocusForge operates on a 'Local-First' architecture. Data is stored in your browser's secure cache. Cloud Sync is an optional opt-in feature for multi-device operators." },
  { q: "What is 'Hard Mode' exactly?", a: "Hard Mode is a cognitive lock. Once a session starts, the 'Pause' and 'Stop' controls are disabled for the first 15 minutes to force you through the 'boredom threshold' into flow state." },
  { q: "Can I export my weekly synthesis reports?", a: "Yes. In the Stats panel, you can generate a CSV or PDF summary of your weekly output, including focus scores and distraction logs for your personal records." },
  { q: "How does the Distraction Vault prevent context switching?", a: "Instead of opening a notes app or browser tab to 'remember' a thought, the Vault is a single-field input always present in the Cockpit. Type it, hit enter, and it's gone from your mind but saved for later." },
];

export default function Demo() {
  const [activeStep, setActiveStep] = useState(1);
  const [dataFilter, setDataFilter] = useState('All');
  const [simulatedState, setSimulatedState] = useState('none');
  const [openFaq, setOpenFaq] = useState(null);

  const filteredData = dataFilter === 'All' 
    ? SAMPLE_SESSIONS 
    : SAMPLE_SESSIONS.filter(s => s.status === dataFilter);

  return (
    <div className="max-w-6xl mx-auto space-y-16 pb-12">
      
      {/* 1. Overview / What it does */}
      <section className="relative overflow-hidden pt-8">
        <div className="absolute top-0 left-0 w-64 h-64 bg-[#00F0FF]/5 blur-[120px] -z-10" />
        <div className="mb-8">
          <h1 className="text-4xl md:text-6xl font-light text-[#F4F1EA] mb-4">Overview / <span className="text-[#00F0FF] font-bold">What it does</span></h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <p className="text-[#8A95A5] text-lg leading-relaxed">
              FocusForge is a <span className="text-[#F4F1EA]">Cognitive Operating System</span> designed for knowledge workers who struggle with fragmented attention. It solves the problem of "shallow work" by creating a high-stakes, distraction-free environment that gamifies deep work sessions. 
              <br /><br />
              The key outcome is <span className="text-[#00F0FF]">Sustained Flow Recovery</span>—helping you get back into the zone faster and stay there longer through neural soundscapes and forced focus constraints.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-[#0A0F1D] border border-[#1A233A] rounded-xl">
                <div className="text-[#00F0FF] text-2xl font-bold font-mono-cyber mb-1">42%</div>
                <div className="text-[10px] uppercase text-[#8A95A5] tracking-widest">Avg. Flow Increase</div>
              </div>
              <div className="p-4 bg-[#0A0F1D] border border-[#1A233A] rounded-xl">
                <div className="text-[#F4F1EA] text-2xl font-bold font-mono-cyber mb-1">11m</div>
                <div className="text-[10px] uppercase text-[#8A95A5] tracking-widest">Saved Per Session</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Demo / How It Works */}
      <section id="demo-flow" className="space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-px bg-[#00F0FF]" />
          <h2 className="text-xl uppercase tracking-[0.3em] text-[#F4F1EA] font-semibold">Demo / How It Works</h2>
        </div>
        
        <CyberCard title="Try this flow" action={<div className="text-[10px] text-[#00F0FF] font-bold font-mono-cyber">SEQUENCE_{activeStep}_OF_7</div>}>
          <div className="flex flex-col md:flex-row gap-8">
            <div className="md:w-1/3 space-y-2 max-h-[400px] overflow-y-auto pr-2 hide-scrollbar">
              {WORKFLOW_STEPS.map((step) => (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg border transition-all flex items-center gap-3 ${
                    activeStep === step.id 
                      ? 'bg-[#00F0FF]/10 border-[#00F0FF] text-[#00F0FF] laser-glow' 
                      : 'bg-[#050914] border-[#1A233A] text-[#8A95A5] hover:border-[#4A5568]'
                  }`}
                >
                  <SafeIcon icon={step.icon} className="text-lg shrink-0" />
                  <span className="text-xs font-bold uppercase tracking-tighter truncate">{step.title}</span>
                </button>
              ))}
            </div>

            <div className="md:w-2/3 bg-[#050914]/60 border border-[#1A233A] rounded-xl p-8 flex flex-col justify-center relative overflow-hidden min-h-[300px]">
              <div className="absolute top-0 right-0 p-4 opacity-[0.03]">
                 <SafeIcon icon={WORKFLOW_STEPS[activeStep-1].icon} className="text-[12rem]" />
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="relative z-10"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-10 h-10 bg-[#00F0FF]/10 rounded-lg flex items-center justify-center border border-[#00F0FF]/30">
                       <SafeIcon icon={WORKFLOW_STEPS[activeStep-1].icon} className="text-[#00F0FF] text-xl" />
                    </div>
                    <h3 className="text-xl font-bold text-[#F4F1EA]">{WORKFLOW_STEPS[activeStep-1].title}</h3>
                  </div>
                  <p className="text-[#8A95A5] text-base leading-relaxed mb-8 max-w-lg">{WORKFLOW_STEPS[activeStep-1].desc}</p>
                  <div className="flex gap-3">
                    <button 
                      disabled={activeStep === 1}
                      onClick={() => setActiveStep(prev => prev - 1)}
                      className="p-2 bg-[#1A233A] text-[#F4F1EA] rounded hover:bg-[#2A344A] disabled:opacity-20 transition-colors"
                    >
                      <SafeIcon icon={FiChevronLeft} />
                    </button>
                    <button 
                      disabled={activeStep === 7}
                      onClick={() => setActiveStep(prev => prev + 1)}
                      className="px-6 py-2 bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/50 rounded font-bold uppercase text-xs hover:bg-[#00F0FF]/20 transition-all laser-glow flex items-center gap-2"
                    >
                      Next Phase <SafeIcon icon={FiChevronRight} />
                    </button>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </CyberCard>
      </section>

      {/* 3. Sample Data & 4. Simulated States */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-px bg-[#00F0FF]" />
            <h2 className="text-xl uppercase tracking-[0.3em] text-[#F4F1EA] font-semibold">Sample Data</h2>
          </div>
          <CyberCard title="Session Archive" action={
            <select 
              onChange={(e) => setDataFilter(e.target.value)}
              className="bg-[#050914] border border-[#1A233A] text-[10px] px-2 py-1 rounded text-[#00F0FF] outline-none"
            >
              <option value="All">All Status</option>
              <option value="Optimal">Optimal</option>
              <option value="Drifted">Drifted</option>
              <option value="Interrupted">Interrupted</option>
            </select>
          }>
            <div className="space-y-3">
              {filteredData.map(s => (
                <div key={s.id} className="p-3 bg-[#050914]/40 border border-[#1A233A] rounded flex justify-between items-center group hover:border-[#00F0FF]/30 transition-all">
                  <div>
                    <div className="text-xs font-bold text-[#F4F1EA] group-hover:text-[#00F0FF] transition-colors">{s.label}</div>
                    <div className="text-[10px] text-[#8A95A5] font-mono-cyber">{s.date} // {s.duration}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-[10px] font-bold uppercase ${
                      s.status === 'Optimal' ? 'text-emerald-400' : s.status === 'Drifted' ? 'text-yellow-400' : 'text-rose-500'
                    }`}>{s.status}</div>
                    <div className="text-xs font-mono-cyber text-[#F4F1EA]">{s.focus}%</div>
                  </div>
                </div>
              ))}
            </div>
          </CyberCard>
        </section>

        <section className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-px bg-[#00F0FF]" />
            <h2 className="text-xl uppercase tracking-[0.3em] text-[#F4F1EA] font-semibold">Simulated States</h2>
          </div>
          <CyberCard title="Interface Preview">
            <div className="grid grid-cols-3 gap-2 mb-4">
              {['Success', 'Empty', 'Warning', 'Error', 'Progress'].map(st => (
                <button 
                  key={st}
                  onClick={() => setSimulatedState(st)}
                  className={`px-2 py-2 text-[9px] uppercase font-bold rounded border transition-all ${
                    simulatedState === st ? 'bg-[#00F0FF] text-[#050914] border-[#00F0FF]' : 'bg-[#050914] border-[#1A233A] text-[#8A95A5]'
                  }`}
                >
                  {st}
                </button>
              ))}
            </div>
            <div className="h-40 border border-[#1A233A] rounded-lg bg-[#050914] flex items-center justify-center p-4">
              <AnimatePresence mode="wait">
                {simulatedState === 'none' && <p key="none" className="text-[#4A5568] text-xs font-mono-cyber">SYS.IDLE // WAITING FOR STATE INPUT</p>}
                {simulatedState === 'Success' && (
                  <motion.div key="success" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-emerald-400 text-center">
                    <SafeIcon icon={FiCheckCircle} className="text-3xl mx-auto mb-2" />
                    <div className="text-xs font-bold uppercase">Archive Synced</div>
                  </motion.div>
                )}
                {simulatedState === 'Empty' && (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[#4A5568] text-center">
                    <SafeIcon icon={FiInfo} className="text-3xl mx-auto mb-2" />
                    <div className="text-xs font-bold uppercase">No Active Rails</div>
                  </motion.div>
                )}
                {simulatedState === 'Warning' && (
                  <motion.div key="warning" initial={{ y: 5, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-yellow-400 text-center">
                    <SafeIcon icon={FiAlertTriangle} className="text-3xl mx-auto mb-2" />
                    <div className="text-xs font-bold uppercase">Link Degradation</div>
                  </motion.div>
                )}
                {simulatedState === 'Error' && (
                  <motion.div key="error" initial={{ x: -10, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="text-rose-500 text-center">
                    <SafeIcon icon={FiShield} className="text-3xl mx-auto mb-2" />
                    <div className="text-xs font-bold uppercase">Auth Violation</div>
                  </motion.div>
                )}
                 {simulatedState === 'Progress' && (
                  <motion.div key="progress" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[#00F0FF] text-center">
                    <div className="w-10 h-10 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                    <div className="text-xs font-bold uppercase">Calibrating...</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <button onClick={() => setSimulatedState('none')} className="w-full mt-3 py-1 text-[8px] uppercase tracking-widest text-[#4A5568] hover:text-[#8A95A5]">Clear Simulation</button>
          </CyberCard>
        </section>
      </div>

      {/* 5. FAQ */}
      <section className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-px bg-[#00F0FF]" />
          <h2 className="text-xl uppercase tracking-[0.3em] text-[#F4F1EA] font-semibold">FAQ</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          {FAQS.map((faq, idx) => (
            <div key={idx} className="border border-[#1A233A] rounded-xl bg-[#0A0F1D]/60 overflow-hidden">
              <button 
                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                className="w-full px-6 py-4 flex justify-between items-center text-left hover:bg-[#00F0FF]/5 transition-colors"
              >
                <span className="text-[#F4F1EA] font-medium text-sm">{faq.q}</span>
                <SafeIcon icon={FiChevronRight} className={`text-[#00F0FF] transition-transform ${openFaq === idx ? 'rotate-90' : ''}`} />
              </button>
              <AnimatePresence>
                {openFaq === idx && (
                  <motion.div 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="px-6 pb-4 overflow-hidden"
                  >
                    <p className="text-[#8A95A5] text-sm leading-relaxed border-t border-[#1A233A] pt-4">{faq.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </section>

      {/* Footer Credit */}
      <footer className="pt-16 pb-8 border-t border-[#1A233A] flex flex-col md:flex-row justify-between items-center gap-4 opacity-40 grayscale hover:grayscale-0 hover:opacity-100 transition-all">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiCpu} className="text-[#00F0FF]" />
          <span className="text-[10px] font-mono-cyber uppercase tracking-widest">FocusForge v2.0.4-Stable</span>
        </div>
        <p className="text-[10px] text-[#8A95A5] tracking-[0.3em] uppercase font-mono-cyber">
          Made by LingLink AI and DigLab AI
        </p>
      </footer>
    </div>
  );
}